import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { PageLoader } from '../components/Loading';
import { formatCurrency, formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { useDataRefresh } from '../context/DataRefreshContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Building2,
  DollarSign,
  Users,
  TrendingUp,
  Award,
  ArrowUpRight,
  AlertCircle,
  TrendingDown,
  Wallet,
  Target,
  Plus,
  Trash2,
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHART_COLORS = ['#22c55e', '#3b82f6', '#a855f7']; // Green, Blue, Purple - distinct colors

// Custom hook for responsive detection
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { refreshTrigger } = useDataRefresh();
  const { isMD } = useAuth();
  const isMobile = useIsMobile();
  
  // Targets state
  const [targets, setTargets] = useState([]);
  const [agents, setAgents] = useState([]);
  const [properties, setProperties] = useState([]);
  const [targetModalOpen, setTargetModalOpen] = useState(false);
  const [targetForm, setTargetForm] = useState({
    agent_id: '',
    property_id: '',
    target_units: '',
    deadline: '',
    notes: '',
  });

  useEffect(() => {
    fetchDashboard();
    fetchTargets();
    if (isMD) {
      fetchAgentsAndProperties();
    }
  }, [refreshTrigger, isMD]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/dashboard`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchTargets = async () => {
    try {
      const response = await axios.get(`${API}/targets`);
      setTargets(response.data);
    } catch (error) {
      console.error('Error fetching targets:', error);
    }
  };

  const fetchAgentsAndProperties = async () => {
    try {
      const [agentsRes, propsRes] = await Promise.all([
        axios.get(`${API}/agents`),
        axios.get(`${API}/properties`)
      ]);
      setAgents(agentsRes.data);
      setProperties(propsRes.data.filter(p => p.available_units > 0));
    } catch (error) {
      console.error('Error fetching agents/properties:', error);
    }
  };

  const handleCreateTarget = async () => {
    if (!targetForm.agent_id || !targetForm.property_id || !targetForm.target_units) {
      toast.error('Please fill in all required fields');
      return;
    }
    try {
      await axios.post(`${API}/targets`, {
        ...targetForm,
        target_units: parseInt(targetForm.target_units),
      });
      toast.success('Target created successfully');
      setTargetModalOpen(false);
      setTargetForm({ agent_id: '', property_id: '', target_units: '', deadline: '', notes: '' });
      fetchTargets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create target');
    }
  };

  const handleDeleteTarget = async (targetId) => {
    if (!window.confirm('Delete this target?')) return;
    try {
      await axios.delete(`${API}/targets/${targetId}`);
      toast.success('Target deleted');
      fetchTargets();
    } catch (error) {
      toast.error('Failed to delete target');
    }
  };

  if (loading) {
    return (
      <Layout>
        <PageLoader />
      </Layout>
    );
  }

  // Filter stats based on role - hide commission info from agents
  const stats = [
    {
      label: 'Total Properties',
      value: data?.total_properties || 0,
      icon: Building2,
      color: 'text-lux-gold',
    },
    {
      label: 'Properties Sold',
      value: data?.properties_sold || 0,
      icon: TrendingUp,
      color: 'text-green-400',
    },
    {
      label: 'Total Sales Value',
      value: formatCurrency(data?.total_sales_value || 0),
      icon: DollarSign,
      color: 'text-lux-gold-light',
      mdOnly: true, // Only MD sees total sales value
    },
    {
      label: 'Active Agents',
      value: data?.active_agents || 0,
      icon: Users,
      color: 'text-blue-400',
    },
    {
      label: 'Commissions Payable',
      value: formatCurrency(data?.total_commissions || 0),
      icon: Award,
      color: 'text-amber-400',
      mdOnly: true, // Only MD sees commissions
    },
  ].filter(stat => !stat.mdOnly || isMD);

  return (
    <Layout>
      <div className="space-y-6 sm:space-y-8 fade-in" data-testid="dashboard-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-2xl sm:text-4xl text-lux-text mb-1 sm:mb-2">Dashboard</h1>
          <p className="text-lux-muted text-sm sm:text-base">Welcome to your real estate command center</p>
        </div>

        {/* Pending Approvals Alert - MD Only */}
        {isMD && data?.pending_approvals > 0 && (
          <div className="bg-amber-900/20 border border-amber-600/50 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-amber-400 font-medium">
                {data.pending_approvals} sale{data.pending_approvals > 1 ? 's' : ''} pending approval
              </p>
              <p className="text-amber-400/70 text-sm">Review and approve sales in the Sales Records page</p>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
          {stats.map((stat, index) => (
            <div
              key={stat.label}
              className={`stat-card group ${index === stats.length - 1 && stats.length % 2 === 1 ? 'col-span-2 sm:col-span-1' : ''}`}
              data-testid={`stat-card-${index}`}
            >
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-lux-muted text-xs sm:text-sm mb-1 truncate">{stat.label}</p>
                  <p className={`font-heading text-lg sm:text-2xl lg:text-3xl ${stat.color} truncate`}>
                    {stat.value}
                  </p>
                </div>
                <div className={`p-1.5 sm:p-2 rounded-lg bg-lux-card ${stat.color} flex-shrink-0 ml-2`}>
                  <stat.icon className="w-4 h-4 sm:w-5 sm:h-5" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Profit & Loss Summary - MD Only */}
        {isMD && data?.profit_loss && (
          <div className="lux-card p-4 sm:p-6">
            <h2 className="font-heading text-xl sm:text-2xl text-lux-text mb-4 sm:mb-6 flex items-center gap-2">
              <Wallet className="w-6 h-6 text-lux-gold" />
              Profit & Loss Overview
            </h2>
            
            {/* P&L Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
              <div className="bg-lux-row p-4 rounded-lg">
                <p className="text-lux-muted text-xs sm:text-sm mb-1">Total Revenue</p>
                <p className="text-green-400 font-heading text-lg sm:text-2xl">{formatCurrency(data.profit_loss.total_revenue)}</p>
              </div>
              <div className="bg-lux-row p-4 rounded-lg">
                <p className="text-lux-muted text-xs sm:text-sm mb-1">Total Costs</p>
                <p className="text-red-400 font-heading text-lg sm:text-2xl">{formatCurrency(data.profit_loss.total_costs)}</p>
              </div>
              <div className="bg-lux-row p-4 rounded-lg">
                <p className="text-lux-muted text-xs sm:text-sm mb-1">Gross Profit</p>
                <p className={`font-heading text-lg sm:text-2xl ${data.profit_loss.gross_profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {formatCurrency(data.profit_loss.gross_profit)}
                </p>
              </div>
              <div className="bg-lux-row p-4 rounded-lg">
                <p className="text-lux-muted text-xs sm:text-sm mb-1">Net Profit (After Commission)</p>
                <p className={`font-heading text-lg sm:text-2xl ${data.profit_loss.net_profit >= 0 ? 'text-lux-gold' : 'text-red-400'}`}>
                  {formatCurrency(data.profit_loss.net_profit)}
                </p>
              </div>
            </div>

            {/* Profit Margin */}
            <div className="flex items-center gap-4 mb-6 p-3 bg-lux-row rounded-lg">
              <div className="flex-1">
                <p className="text-lux-muted text-sm">Profit Margin</p>
                <div className="flex items-center gap-2">
                  {data.profit_loss.profit_margin >= 0 ? (
                    <TrendingUp className="w-5 h-5 text-green-400" />
                  ) : (
                    <TrendingDown className="w-5 h-5 text-red-400" />
                  )}
                  <span className={`font-heading text-2xl ${data.profit_loss.profit_margin >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {data.profit_loss.profit_margin.toFixed(1)}%
                  </span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lux-muted text-sm">Commissions Paid</p>
                <p className="text-amber-400 font-semibold">{formatCurrency(data.profit_loss.total_commissions_paid)}</p>
              </div>
            </div>

            {/* Property P&L Breakdown */}
            {data.profit_loss.property_breakdown?.length > 0 && (
              <div>
                <h3 className="text-lux-muted text-sm uppercase tracking-wider mb-3">Sales Breakdown (Top Performers)</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-lux-border">
                        <th className="text-left py-2 text-lux-muted font-medium">Property / Sale</th>
                        <th className="text-center py-2 text-lux-muted font-medium">Units</th>
                        <th className="text-right py-2 text-lux-muted font-medium">Revenue</th>
                        <th className="text-right py-2 text-lux-muted font-medium">Cost</th>
                        <th className="text-right py-2 text-lux-muted font-medium">Profit</th>
                        <th className="text-right py-2 text-lux-muted font-medium">Margin</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.profit_loss.property_breakdown.slice(0, 5).map((prop, idx) => (
                        <tr key={`${prop.property_id}-${idx}`} className="border-b border-lux-border/50">
                          <td className="py-2">
                            <p className="text-lux-text">{prop.property_name}</p>
                            <p className="text-xs text-lux-muted">
                              {prop.sale_code} • {prop.cost_per_unit > 0 ? `₦${(prop.cost_per_unit || 0).toLocaleString()}/${prop.unit_type || 'unit'}` : 'No cost data'}
                            </p>
                          </td>
                          <td className="py-2 text-center">
                            <span className="px-2 py-0.5 bg-blue-900/20 text-blue-400 rounded text-xs">
                              {prop.units_purchased || 1} {prop.unit_type || 'unit'}
                            </span>
                          </td>
                          <td className="py-2 text-right text-green-400">{formatCurrency(prop.revenue)}</td>
                          <td className="py-2 text-right text-red-400">
                            {prop.cost_per_unit > 0 ? formatCurrency(prop.total_cost) : <span className="text-lux-muted">N/A</span>}
                          </td>
                          <td className={`py-2 text-right font-semibold ${prop.profit >= 0 ? 'text-lux-gold' : 'text-red-400'}`}>
                            {prop.cost_per_unit > 0 ? formatCurrency(prop.profit) : <span className="text-lux-muted">-</span>}
                          </td>
                          <td className={`py-2 text-right ${prop.profit_margin >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {prop.cost_per_unit > 0 ? `${prop.profit_margin.toFixed(1)}%` : <span className="text-lux-muted">-</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Recent Sales */}
          <div className="lux-card p-4 sm:p-6">
            <h2 className="font-heading text-xl sm:text-2xl text-lux-text mb-3 sm:mb-4">
              {isMD ? 'Recent Sales' : 'My Recent Sales'}
            </h2>
            <div className="space-y-2 sm:space-y-3">
              {data?.recent_sales?.length > 0 ? (
                data.recent_sales.map((sale) => (
                  <div
                    key={sale.id}
                    className="flex items-center justify-between p-3 sm:p-4 bg-lux-row rounded-lg hover:bg-lux-row/70 transition-colors"
                  >
                    <div className="flex-1 min-w-0 mr-3">
                      <p className="text-lux-text font-medium truncate text-sm sm:text-base">
                        {sale.property?.name || 'N/A'}
                      </p>
                      <p className="text-lux-muted text-xs sm:text-sm truncate">
                        {isMD ? (sale.primary_agent?.full_name || 'MD Office') + ' • ' : ''}{formatDate(sale.sale_date)}
                      </p>
                    </div>
                    {/* Only MD sees sale amounts */}
                    {isMD && (
                      <div className="text-right flex-shrink-0">
                        <p className="text-lux-gold font-semibold text-sm sm:text-base">
                          {formatCurrency(sale.sale_amount)}
                        </p>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-lux-muted text-center py-8 text-sm">No sales yet</p>
              )}
            </div>
          </div>

          {/* Top Agents */}
          <div className="lux-card p-4 sm:p-6">
            <h2 className="font-heading text-xl sm:text-2xl text-lux-text mb-3 sm:mb-4">Top Agents</h2>
            <div className="space-y-2 sm:space-y-3">
              {data?.top_agents?.length > 0 ? (
                data.top_agents.map((agent, index) => (
                  <div
                    key={agent.id}
                    className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-lux-row rounded-lg hover:bg-lux-row/70 transition-colors"
                  >
                    <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center font-bold text-xs sm:text-sm flex-shrink-0 ${
                      index === 0 ? 'bg-lux-gold text-lux-black' :
                      index === 1 ? 'bg-gray-400 text-lux-black' :
                      index === 2 ? 'bg-amber-600 text-lux-black' :
                      'bg-lux-border text-lux-muted'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-lux-text font-medium truncate text-sm sm:text-base">{agent.full_name}</p>
                      <p className="text-lux-muted text-xs sm:text-sm">{agent.deal_count} deals</p>
                    </div>
                    {/* Only MD sees the sales value amounts */}
                    {isMD && (
                      <div className="text-right flex-shrink-0">
                        <p className="text-lux-gold font-semibold text-sm sm:text-base">
                          {formatCurrency(agent.total_value)}
                        </p>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-lux-muted text-center py-8 text-sm">No agent data</p>
              )}
            </div>
          </div>
        </div>

        {/* Sales by Source Chart */}
        <div className="lux-card p-4 sm:p-6">
          <h2 className="font-heading text-xl sm:text-2xl text-lux-text mb-4 sm:mb-6">Sales by Source</h2>
          <div className="h-64 sm:h-80">
            {data?.sales_by_source?.some(item => item.value > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data.sales_by_source.filter(item => item.value > 0)}
                    cx="50%"
                    cy="50%"
                    innerRadius={isMobile ? 40 : 60}
                    outerRadius={isMobile ? 70 : 100}
                    paddingAngle={5}
                    dataKey="value"
                    label={!isMobile ? ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
                      const RADIAN = Math.PI / 180;
                      const radius = outerRadius + 30;
                      const x = cx + radius * Math.cos(-midAngle * RADIAN);
                      const y = cy + radius * Math.sin(-midAngle * RADIAN);
                      return (
                        <text 
                          x={x} 
                          y={y} 
                          fill="#ffffff"
                          textAnchor={x > cx ? 'start' : 'end'}
                          dominantBaseline="central"
                          style={{ 
                            fontSize: '14px', 
                            fontWeight: '600',
                            textShadow: '0 2px 4px rgba(0,0,0,0.8)'
                          }}
                        >
                          {`${name} (${(percent * 100).toFixed(0)}%)`}
                        </text>
                      );
                    } : false}
                    labelLine={!isMobile ? { stroke: '#ffffff', strokeWidth: 2 } : false}
                  >
                    {data.sales_by_source.filter(item => item.value > 0).map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={CHART_COLORS[index % CHART_COLORS.length]}
                        stroke="#070c1a"
                        strokeWidth={2}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#0a1428',
                      border: '1px solid #1e2a45',
                      borderRadius: '8px',
                      color: '#ffffff',
                      fontSize: '14px',
                      fontWeight: '500',
                    }}
                    formatter={(value, name) => [`${value} sales`, name]}
                  />
                  <Legend
                    wrapperStyle={{ 
                      color: '#ffffff', 
                      fontSize: isMobile ? '12px' : '14px',
                      fontWeight: '600',
                    }}
                    formatter={(value) => (
                      <span style={{ 
                        color: '#ffffff', 
                        fontSize: isMobile ? '12px' : '14px',
                        fontWeight: '600',
                      }}>
                        {value}
                      </span>
                    )}
                    layout="horizontal"
                    align="center"
                    verticalAlign="bottom"
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-lux-muted">
                No sales data to display
              </div>
            )}
          </div>
        </div>

        {/* Sales Targets Section */}
        <div className="lux-card p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-lux-gold" />
              <h2 className="font-heading text-xl sm:text-2xl text-lux-text">
                {isMD ? 'Sales Targets' : 'My Targets'}
              </h2>
            </div>
            {isMD && (
              <Button
                onClick={() => setTargetModalOpen(true)}
                className="lux-btn-primary"
                size="sm"
                data-testid="add-target-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Set Target
              </Button>
            )}
          </div>
          
          {targets.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {targets.map((target) => (
                <div 
                  key={target.id} 
                  className="p-4 bg-lux-row rounded-lg border border-lux-border"
                  data-testid={`target-card-${target.id}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="text-lux-text font-medium text-sm">{target.property?.name || 'Unknown Property'}</p>
                      {isMD && (
                        <p className="text-lux-muted text-xs">{target.agent?.full_name || 'Unknown Agent'}</p>
                      )}
                    </div>
                    {isMD && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteTarget(target.id)}
                        className="text-red-400 hover:text-red-300 h-6 w-6 p-0"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                  
                  <div className="mb-2">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-lux-muted">Progress</span>
                      <span className={`font-semibold ${target.progress_percent >= 100 ? 'text-green-400' : 'text-lux-gold'}`}>
                        {target.achieved_units}/{target.target_units} {target.property?.unit_type || 'unit'}s
                      </span>
                    </div>
                    <Progress 
                      value={target.progress_percent} 
                      className="h-2"
                    />
                  </div>
                  
                  <div className="flex justify-between items-center text-xs">
                    <span className={`px-2 py-0.5 rounded ${
                      target.progress_percent >= 100 
                        ? 'bg-green-900/30 text-green-400' 
                        : target.progress_percent >= 50 
                          ? 'bg-amber-900/30 text-amber-400'
                          : 'bg-blue-900/30 text-blue-400'
                    }`}>
                      {target.progress_percent >= 100 ? 'Completed' : `${Math.round(target.progress_percent)}%`}
                    </span>
                    {target.deadline && (
                      <span className="text-lux-muted">Due: {formatDate(target.deadline)}</span>
                    )}
                  </div>
                  
                  {target.notes && (
                    <p className="text-lux-muted text-xs mt-2 italic">"{target.notes}"</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Target className="w-12 h-12 text-lux-border mx-auto mb-2" />
              <p className="text-lux-muted">
                {isMD ? 'No targets set yet. Click "Set Target" to create one.' : 'No targets assigned to you yet.'}
              </p>
            </div>
          )}
        </div>

        {/* Target Creation Modal (MD only) */}
        {isMD && (
          <Dialog open={targetModalOpen} onOpenChange={setTargetModalOpen}>
            <DialogContent className="bg-lux-card border-lux-border max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-lux-gold">Set Sales Target</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label className="text-lux-text">Agent *</Label>
                  <Select
                    value={targetForm.agent_id}
                    onValueChange={(val) => setTargetForm({ ...targetForm, agent_id: val })}
                  >
                    <SelectTrigger className="lux-input">
                      <SelectValue placeholder="Select agent" />
                    </SelectTrigger>
                    <SelectContent>
                      {agents.map((agent) => (
                        <SelectItem key={agent.id} value={agent.id}>
                          {agent.full_name} ({agent.agent_code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-lux-text">Property *</Label>
                  <Select
                    value={targetForm.property_id}
                    onValueChange={(val) => setTargetForm({ ...targetForm, property_id: val })}
                  >
                    <SelectTrigger className="lux-input">
                      <SelectValue placeholder="Select property" />
                    </SelectTrigger>
                    <SelectContent>
                      {properties.map((prop) => (
                        <SelectItem key={prop.id} value={prop.id}>
                          {prop.name} ({prop.available_units} {prop.unit_type}s available)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-lux-text">Target Units *</Label>
                  <Input
                    type="number"
                    min="1"
                    value={targetForm.target_units}
                    onChange={(e) => setTargetForm({ ...targetForm, target_units: e.target.value })}
                    className="lux-input"
                    placeholder="e.g., 5"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-lux-text">Deadline (optional)</Label>
                  <Input
                    type="date"
                    value={targetForm.deadline}
                    onChange={(e) => setTargetForm({ ...targetForm, deadline: e.target.value })}
                    className="lux-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-lux-text">Notes (optional)</Label>
                  <Input
                    value={targetForm.notes}
                    onChange={(e) => setTargetForm({ ...targetForm, notes: e.target.value })}
                    className="lux-input"
                    placeholder="e.g., Q1 target for new development"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setTargetModalOpen(false)}
                  className="lux-btn-secondary"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateTarget}
                  className="lux-btn-primary"
                >
                  Create Target
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Layout>
  );
}
